/**
 * Root route dispatcher.
 *
 * - `dashboard.*` host → HomePage (the dashboard subdomain is for the app,
 *   never marketing — root URL there means "log in")
 * - Custom-branded subdomain → HomePage (their login IS their landing)
 * - Generic + auth-related URL params → HomePage (handles verify-email,
 *   magic-link, OAuth callback, trial token, register mode)
 * - Otherwise → MarketingHome (Serac product marketing)
 *
 * Authenticated users still see MarketingHome — the marketing site
 * is the public face of the product, available to everyone. The
 * TopNav's "Open dashboard" CTA gets them back to the app via /login,
 * which auto-redirects when a session is present.
 */
import { lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';
import MarketingHome from './MarketingHome';

// Home is the auth-flow landing — only rendered when a search param
// signals login / register / verify / etc., or when the host carries
// custom branding. The vast majority of /-visits hit MarketingHome,
// so Home (which pulls in SeracLogin, Turnstile, the auth API client,
// etc.) does not belong in the initial marketing bundle.
const HomePage = lazy(() => import('./Home'));

export default function IndexPage() {
  const { branding, isLoading: brandingLoading } = useBranding();
  const [searchParams] = useSearchParams();

  if (brandingLoading) return null;

  const isCustomBranded = branding.type !== null;

  const isDashboardHost =
    typeof window !== 'undefined' && window.location.hostname.startsWith('dashboard.');

  const hasAuthParam =
    searchParams.has('tab') ||
    searchParams.has('mode') ||
    searchParams.has('token') ||
    searchParams.has('trial_token') ||
    searchParams.has('returnUrl');

  if (isDashboardHost || isCustomBranded || hasAuthParam) {
    return (
      <Suspense fallback={null}>
        <HomePage />
      </Suspense>
    );
  }
  return <MarketingHome />;
}
